import './Notify.sass';

import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute, useRouter } from 'react-router5';

import {
  Button,
  HeaderSelect,
  LottieIcon,
  Placeholder,
  PullToRefresh,
  ScrollView,
} from '$uikit';
import { notifyActions, notifySelector } from '$store/notify';
import { Loader, MetaTags, NotifyItem, NotifyRequest } from '$shared/components';
import { isBackNavigation } from '$navigation/router';
import { useCurrentLang, useTranslator } from '$hooks';
import { NOTIFY, NOTIFY_SETTINGS } from '$shared/constants/pages';
import {
  IcComment24,
  IcLike24,
  IcNotifications28,
  IcUsers28,
  IcSettings28,
  IcDollar24,
  IcStar28,
  IcInfo28,
  IcCalendar28,
} from '$assets';
import { NotifyModel, NotifyModelType } from '$store/models';
import { dateFnsLocale } from '$utils';
import { groupBy } from 'lodash';
import {
  differenceInDays,
  differenceInSeconds,
  formatDistanceToNowStrict,
} from 'date-fns';
import { mainActions } from '$store/main';
import { NotifyDateSelector } from '$core/Notify/NotifyDateSelector';

export const Notify: FC = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { isLoading, items, isLoadingMore, nextFrom } = useSelector(notifySelector);
  const isBack = isBackNavigation();
  const t = useTranslator();
  const { route } = useRoute();
  const activeTab = route.params.tab || 'all';
  const currentLang = useCurrentLang();
  const from = parseInt(route.params.from || '0', 10);
  const to = parseInt(route.params.to || '0', 10);

  const isDateSelected = from > 0 || to > 0;

  useEffect(() => {
    if (!isBack) {
      dispatch(notifyActions.load());
    }
  }, []);

  const handleRefresh = useCallback(() => {
    dispatch(notifyActions.load());
  }, [dispatch, activeTab]);

  const handleChangeTab = useCallback(
    (newTab: string) => {
      const params: any = { ...route.params };
      if (newTab !== 'all') {
        params.tab = newTab;
      } else {
        delete params.tab;
      }
      router.navigate(NOTIFY, params, { replace: true });
      dispatch(notifyActions.load());

      const scrollNode = document.querySelector('._ScrollView');
      if (scrollNode) {
        scrollNode.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    [route.params],
  );

  const handleDate = () => {
    dispatch(mainActions.setPopup(<NotifyDateSelector />));
  };

  const handleMore = () => {
    dispatch(notifyActions.loadMore());
  };

  const notificationsGroupTitle = useCallback(
    (n: NotifyModel): string => {
      const now = new Date();
      const date = new Date(n.createdAt * 1000);
      const diffInDays = differenceInDays(now, date);
      const locale = dateFnsLocale[currentLang.key] ?? dateFnsLocale.en;
      if (differenceInSeconds(now, date) < 5) {
        return t('time_now');
      } else if (diffInDays < 7) {
        return formatDistanceToNowStrict(date, { addSuffix: true, locale });
      }
      return t('notify_title_earlier');
    },
    [currentLang],
  );

  const groupedItems = useMemo(() => {
    const grouped = groupBy(items, notificationsGroupTitle);
    return Object.keys(grouped).map((date) => {
      return {
        date,
        items: grouped[date],
      };
    });
  }, [items]);

  function renderContent() {
    if (isLoading && !items.length) {
      return <Loader isWrapped />;
    }

    if (!items.length) {
      return (
        <Placeholder centered iconSize={120} icon={<LottieIcon name="notify" />}>
          {t('notify_empty')}
        </Placeholder>
      );
    }

    return (
      <>
        {groupedItems.map((group) => (
          <>
            <div key={group.date} className="Notify__group__title">
              {group.date}
            </div>
            {group.items.map((item) => (
              <NotifyItem notify={item} key={item.id} />
            ))}
          </>
        ))}
        {isLoadingMore && (
          <div style={{ padding: 32 }}>
            <Loader isWrapped />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <MetaTags title={t('notify_title')} />
      <HeaderSelect
        showBackButton
        rightIcons={[
          {
            icon: <IcSettings28 />,
            onClick: () => {
              router.navigate(NOTIFY_SETTINGS);
            },
          },
        ]}
        value={activeTab}
        onChange={handleChangeTab}
      >
        <option value={NotifyModelType.ALL}>
          <IcNotifications28 />
          {t('notify_type_all')}
        </option>
        <option value={NotifyModelType.LIKE}>
          <IcLike24 />
          {t('notify_type_like')}
        </option>
        <option value={NotifyModelType.FOLLOW}>
          <IcUsers28 />
          {t('notify_type_follow')}
        </option>
        <option value={NotifyModelType.COMMENT}>
          <IcComment24 />
          {t('notify_type_comment')}
        </option>
        <option value={NotifyModelType.SALES}>
          <IcDollar24 />
          {t('notify_type_sales')}
        </option>
        <option value={NotifyModelType.NEW_SUBSCRIBERS}>
          <IcStar28 />
          {t('notify_type_sponsors')}
        </option>
        <option value={NotifyModelType.SYSTEM}>
          <IcInfo28 />
          {t('notify_type_system')}
        </option>
      </HeaderSelect>
      <ScrollView
        isLoadingMore={isLoadingMore}
        canLoadMore={nextFrom.length > 0}
        onLoadMore={handleMore}
      >
        <PullToRefresh isRefreshing={isLoading} onRefresh={handleRefresh}>
          <div className="NotifyFilters">
            <Button
              icon={<IcCalendar28 />}
              size="small"
              mode={isDateSelected ? 'light' : 'secondary'}
              onClick={handleDate}
            >
              {t('notify_filter_date')}
            </Button>
          </div>
          {(!isLoading || items.length > 0) && <NotifyRequest />}
          {renderContent()}
        </PullToRefresh>
      </ScrollView>
    </>
  );
};
